import { useState, useEffect } from "react";
import Layout from "../components/Layout.jsx";
import { chatWidget, getAccount, reportOps } from "../lib/supabase.js";

// ═══ Reports — AI-drafted, human-edited, exported with tenant branding ═══

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const C = { ink: "#1a1a1a", dim: "#8a8a8a", faint: "#c0bdb8", line: "#e5e3de", bg: "#fafaf8", card: "#fff", accent: "#6366f1" };
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => iso(new Date(Date.now() - n * 86400000));
const METRIC_GROUPS = [
  { group: "The Funnel", items: [
    ["funnel", "Customer journey funnel", "Google \u2192 site \u2192 assistant \u2192 lead"],
  ]},
  { group: "Website (Google Analytics)", items: [
    ["ga4_summary", "Traffic totals", "visits, visitors, pageviews, bounce"],
    ["ga4_daily", "Daily traffic trend", "day-by-day, powers predictions"],
    ["channels", "Traffic channels", "organic, direct, referral, paid"],
    ["sources", "Traffic sources", "which sites send visitors"],
    ["landing_pages", "Landing pages", "where visitors arrive"],
    ["top_pages", "Most viewed pages", "what they read"],
    ["devices", "Devices", "desktop vs mobile vs tablet"],
    ["geo", "Countries", "where visitors are"],
    ["events", "Site interactions", "clicks, forms, downloads"],
    ["visitor_types", "New vs returning", "fresh faces vs regulars"],
  ]},
  { group: "Google Search", items: [
    ["gsc_summary", "Search totals", "impressions, clicks, CTR, rank"],
    ["gsc_daily", "Daily search trend", "day-by-day, powers predictions"],
    ["gsc_queries", "Top search queries", "what people Google"],
    ["gsc_pages", "Top pages in search", "which pages win clicks"],
    ["gsc_devices", "Search by device", ""],
    ["gsc_countries", "Search by country", ""],
  ]},
  { group: "The Assistant", items: [
    ["chat_daily", "Conversation volume", "chats per day"],
    ["chat_intents", "What people ask", "top conversation topics"],
    ["chat_sentiment", "Sentiment", "how conversations feel"],
    ["chat_products", "Products discussed", "what comes up in chat"],
    ["product_opps", "Product opportunities", "demand without a product match"],
    ["weak_spots", "Assistant weak spots", "questions it fumbles \u2014 training fuel"],
    ["response_stats", "Response performance", "speed, tokens, ratings"],
  ]},
];
const ALL_METRIC_IDS = METRIC_GROUPS.flatMap(g => g.items.map(([id]) => id));

const fmt = (n) => { const v = parseFloat(n) || 0; return v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toLocaleString(); };

export default function Reports() {
  const account = getAccount();
  const [start, setStart] = useState(daysAgo(28));
  const [end, setEnd] = useState(iso(new Date()));
  const [report, setReport] = useState(null);
  const [preview, setPreview] = useState(null);
  const [view, setView] = useState("setup");
  const [selected, setSelected] = useState(new Set(ALL_METRIC_IDS));
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState({});

  const callReportOps = (action) => reportOps(action, { start, end, metrics: [...selected] });

  const loadPreview = async () => {
    setPreviewing(true); setError(null);
    try {
      const d = await callReportOps("preview_data");
      if (d.error) setError(d.error); else setPreview(d);
    } catch (e) { setError(e.message); }
    setPreviewing(false);
  };

  useEffect(() => {
    chatWidget.get("default").then(({ data }) => setBrand(data || {}));
    loadPreview();
  }, []); // eslint-disable-line

  const generate = async () => {
    setGenerating(true); setError(null);
    try {
      const d = await callReportOps("generate_report");
      if (d.error) setError(d.error);
      else { setReport(d); setView("report"); }
    } catch (e) { setError(e.message); }
    setGenerating(false);
  };

  const editSection = (id, content) => setReport(r => ({ ...r, sections: r.sections.map(s => s.id === id ? { ...s, content } : s) }));
  const editTitle = (id, title) => setReport(r => ({ ...r, sections: r.sections.map(s => s.id === id ? { ...s, title } : s) }));

  const brandColor = brand.primary_color || C.accent;
  const f = report?.stats?.funnel || {};

  return (
    <Layout>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-report, #printable-report * { visibility: visible; }
          #printable-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; border: none !important; }
          #printable-report textarea { border: none !important; resize: none; }
          .no-print { display: none !important; }
        }
        .rep-section textarea { width: 100%; border: 1px dashed transparent; background: transparent; font-family: ${sans}; font-size: 13.5px; line-height: 1.7; color: ${C.ink}; resize: vertical; outline: none; padding: 4px; border-radius: 6px; }
        .rep-section textarea:hover, .rep-section textarea:focus { border-color: ${C.line}; background: #fcfcfa; }
      `}</style>
      <div style={{ fontFamily: sans, color: C.ink, maxWidth: 1000 }}>
        <div className="no-print">
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontFamily: serif, fontStyle: "italic", fontWeight: 400, fontSize: 34 }}>Reports</h1>
            <span style={{ fontSize: 12, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>AI-drafted &middot; you edit &middot; export as PDF</span>
          </div>
          <p style={{ margin: "0 0 22px", fontSize: 14, color: C.dim, maxWidth: 620, lineHeight: 1.6 }}>
            The AI reads every metric in the range &mdash; search, traffic, the assistant, leads &mdash; drafts the story with predictions and recommended actions, and you edit anything before exporting a branded PDF.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 26, flexWrap: "wrap" }}>
            <input type="date" value={start} max={end} onChange={e => setStart(e.target.value)} style={inp} />
            <span style={{ color: C.faint, fontSize: 13 }}>to</span>
            <input type="date" value={end} min={start} max={iso(new Date())} onChange={e => setEnd(e.target.value)} style={inp} />
            <button onClick={loadPreview} disabled={previewing} style={{ background: "none", color: C.ink, border: `1px solid ${C.line}`, borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: previewing ? "default" : "pointer", fontFamily: sans }}>
              {previewing ? "Checking…" : "Preview data"}
            </button>
            <button onClick={generate} disabled={generating} style={{ background: C.ink, color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: generating ? "default" : "pointer", fontFamily: sans, opacity: generating ? 0.6 : 1 }}>
              {generating ? "Reading the data & drafting\u2026" : report ? "Regenerate draft" : "Generate report"}
            </button>
            {report && view === "report" && (
              <button onClick={() => setView("setup")} style={{ background: "none", color: C.ink, border: `1px solid ${C.line}`, borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
                &larr; Back to data selection
              </button>
            )}
            {report && view === "setup" && (
              <button onClick={() => setView("report")} style={{ background: "none", color: C.ink, border: `1px solid ${C.line}`, borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
                View report &rarr;
              </button>
            )}
            {report && view === "report" && (
              <button onClick={() => window.print()} style={{ background: "none", color: C.ink, border: `1px solid ${C.ink}`, borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
                Export PDF
              </button>
            )}
          </div>
          {error && <div style={{ background: "#fdf2f0", border: "1px solid #e8c5be", color: "#c0503f", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 20 }}>{error}</div>}
          {preview && !preview.version && (
            <div style={{ background: "#fdf6ec", border: "1px solid #e8d5ae", color: "#8a6a1f", borderRadius: 8, padding: "12px 16px", fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
              <b>The report engine is running an older version.</b> This page and the report-ops function deploy separately —
              paste the current <code style={{ fontSize: 12 }}>supabase\functions\report-ops\index.ts</code> into the master
              Supabase dashboard (Edge Functions &rarr; report-ops) and redeploy, then hit Preview data again. Until then,
              most datasets will show "no data" even where data exists, and metric selection won't affect the report.
            </div>
          )}
          {preview && view === "setup" && (
            <div style={{ border: `1px solid ${C.line}`, borderRadius: 12, padding: "20px 24px", marginBottom: 20, background: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.dim }}>
                  Choose what goes into this report
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => setSelected(new Set(ALL_METRIC_IDS))} style={linkBtn}>Select all</button>
                  <button onClick={() => setSelected(new Set(["funnel", "ga4_summary", "gsc_summary"]))} style={linkBtn}>Essentials only</button>
                </div>
              </div>
              <p style={{ margin: "0 0 14px", fontSize: 11.5, color: C.faint }}>
                Tick a dataset to include it. The number shows how much data exists in your range &mdash; red means empty. Re-run Preview after changing dates.
              </p>
              {METRIC_GROUPS.map(g => (
                <div key={g.group} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.faint, marginBottom: 7 }}>{g.group}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px 14px" }}>
                    {g.items.map(([id, label, sub]) => {
                      const cov = preview.coverage?.[id];
                      const rows = cov ? cov.rows : null;
                      const on = selected.has(id);
                      return (
                        <div key={id} style={{ border: `1px solid ${on ? C.line : "transparent"}`, borderRadius: 8, padding: "8px 10px", background: on ? "#fcfcfa" : "transparent" }}>
                          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", opacity: on ? 1 : 0.5 }}>
                            <input type="checkbox" checked={on} onChange={() => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })} style={{ marginTop: 3 }} />
                            <span style={{ fontSize: 12.5, lineHeight: 1.4, fontWeight: 600 }}>
                              {label}
                              {sub && <span style={{ display: "block", fontSize: 10.5, color: C.faint, fontWeight: 400 }}>{sub}</span>}
                            </span>
                          </label>
                          {on && <div style={{ marginTop: 7, paddingLeft: 24 }}><Peek data={preview.stats?.[id] ?? (id === "funnel" ? preview.stats?.funnel : null)} /></div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 18, fontSize: 12, color: C.dim, borderTop: `1px solid ${C.line}`, paddingTop: 12, flexWrap: "wrap" }}>
                <span>Funnel in range: <b style={{ color: C.ink }}>{fmt(preview.stats?.funnel?.impressions)}</b> seen &rarr; <b style={{ color: C.ink }}>{fmt(preview.stats?.funnel?.clicks)}</b> clicked &rarr; <b style={{ color: C.ink }}>{fmt(preview.stats?.funnel?.sessions)}</b> visited &rarr; <b style={{ color: C.ink }}>{fmt(preview.stats?.funnel?.chat_sessions)}</b> chatted &rarr; <b style={{ color: C.ink }}>{fmt(preview.stats?.funnel?.leads)}</b> leads</span>
                <span style={{ marginLeft: "auto" }}>{selected.size} of {ALL_METRIC_IDS.length} datasets selected</span>
              </div>
            </div>
          )}
          {!report && !generating && !preview && !previewing && view === "setup" && (
            <div style={{ border: `1px dashed ${C.line}`, borderRadius: 12, padding: 40, textAlign: "center", fontSize: 13, color: C.faint, lineHeight: 1.7 }}>
              Pick a date range and preview what data is available, then generate. Drafting takes ~15 seconds.
            </div>
          )}
        </div>

        {report && view === "report" && (
          <div id="printable-report" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: "44px 52px" }}>
            {/* ── Branded header ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 22, borderBottom: `3px solid ${brandColor}`, marginBottom: 30 }}>
              <div>
                <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 30, lineHeight: 1.1 }}>{account?.name || "Performance"} &mdash; Performance Report</div>
                <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>
                  {report.stats?.range?.start} to {report.stats?.range?.end} &middot; Generated {new Date(report.generated_at).toLocaleDateString()} &middot; Powered by Clarifico
                </div>
              </div>
              {brand.logo_url && <img src={brand.logo_url} alt="" style={{ height: 44, objectFit: "contain" }} />}
            </div>

            {/* ── Headline numbers ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0, border: `1px solid ${C.line}`, borderRadius: 10, overflow: "hidden", marginBottom: 34 }}>
              {[
                ["Seen in Google", fmt(f.impressions)], ["Clicks from Google", fmt(f.clicks)],
                ["Site visits", fmt(f.sessions)], ["Conversations", fmt(f.chat_sessions)],
                ["Leads", fmt(f.leads)], ["Escalations", fmt(f.escalations)],
              ].map(([label, v], i) => (
                <div key={label} style={{ padding: "16px 14px", borderLeft: i > 0 ? `1px solid ${C.line}` : "none" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: C.dim, marginBottom: 7 }}>{label}</div>
                  <div style={{ fontSize: 21, fontWeight: 700, color: brandColor }}>{v}</div>
                </div>
              ))}
            </div>

            {/* ── Editable sections ── */}
            <div className="no-print" style={{ fontSize: 11.5, color: C.faint, marginBottom: 16, padding: "8px 12px", background: "#fcfcf9", border: `1px dashed ${C.line}`, borderRadius: 8 }}>
              Every title and paragraph below is editable &mdash; click into any text and rewrite it. The PDF exports exactly what you see.
            </div>
            {report.sections?.map(sec => (
              <div key={sec.id} className="rep-section" style={{ marginBottom: 30 }}>
                <input value={sec.title} onChange={e => editTitle(sec.id, e.target.value)}
                  style={{ fontFamily: serif, fontStyle: "italic", fontSize: 21, border: "none", background: "transparent", outline: "none", color: C.ink, width: "100%", marginBottom: 8, borderBottom: `1px solid ${C.line}`, paddingBottom: 6 }} />
                <textarea value={sec.content} onChange={e => editSection(sec.id, e.target.value)}
                  rows={Math.max(3, Math.ceil(sec.content.length / 110))} />
              </div>
            ))}

            {/* ── Data exhibits: every selected dataset, form chosen per exhibit ── */}
            <div style={{ marginTop: 8, marginBottom: 26 }}>
              <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 21, borderBottom: `1px solid ${C.line}`, paddingBottom: 6, marginBottom: 6 }}>The Numbers</div>
              <div className="no-print" style={{ fontSize: 11, color: C.faint, marginBottom: 16 }}>
                Every dataset you selected, in full. Switch each between chart and numbers, or hide it &mdash; the PDF exports exactly what's shown.
              </div>
              {METRIC_GROUPS.flatMap(g => g.items).filter(([id]) => report.stats?.[id] != null || id === "funnel").map(([id, label]) => (
                <Exhibit key={id} label={label} data={id === "funnel" ? report.stats?.funnel : report.stats?.[id]} />
              ))}
            </div>

            <div style={{ paddingTop: 18, borderTop: `1px solid ${C.line}`, fontSize: 10.5, color: C.faint }}>
              Prepared with Clarifico Arc &middot; {account?.name} &middot; Confidential
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function firstNumericKey(row, skip = ["metric_date"]) {
  for (const [k, v] of Object.entries(row || {})) {
    if (skip.includes(k)) continue;
    if (v !== null && v !== "" && !isNaN(parseFloat(v))) return k;
  }
  return null;
}
function labelKeyOf(row) {
  for (const k of ["dim_value", "query", "source", "intent_label", "product_name", "name", "query_text", "page"]) if (row?.[k] != null) return k;
  for (const [k, v] of Object.entries(row || {})) if (typeof v === "string" && isNaN(parseFloat(v))) return k;
  return null;
}
function Peek({ data }) {
  if (data == null) return <PeekEmpty />;
  const obj = Array.isArray(data) && data.length === 1 && !firstNumericKey(data[0], []) === false && data.length === 1 && !data[0]?.metric_date && !labelKeyOf(data[0]) ? data[0] : data;
  // Summary object (or 1-row array of totals)
  const asObj = !Array.isArray(data) ? data : (data.length === 1 && !data[0]?.metric_date && !labelKeyOf(data[0])) ? data[0] : null;
  if (asObj) {
    const entries = Object.entries(asObj).filter(([, v]) => v !== null && !isNaN(parseFloat(v))).slice(0, 6);
    if (!entries.length) return <PeekEmpty />;
    return (
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {entries.map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt(v)}{/rate|ctr/.test(k) && parseFloat(v) <= 1 ? "" : ""}</div>
            <div style={{ fontSize: 9.5, color: C.faint, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.replace(/_/g, " ").replace(/^avg /, "avg ")}</div>
          </div>
        ))}
      </div>
    );
  }
  if (!Array.isArray(data) || data.length === 0) return <PeekEmpty />;
  // Time series → sparkline
  if (data[0]?.metric_date) {
    const key = firstNumericKey(data[0]);
    if (!key) return <PeekEmpty />;
    const vals = data.map(r => parseFloat(r[key]) || 0);
    const max = Math.max(1, ...vals);
    const W = 220, H = 36;
    const pts = vals.map((v, i) => `${(i / Math.max(1, vals.length - 1)) * W},${H - (v / max) * (H - 4) - 2}`).join(" ");
    return (
      <div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 240, height: 36, display: "block" }}>
          <polyline points={pts} fill="none" stroke={C.accent} strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
        <div style={{ fontSize: 9.5, color: C.faint }}>{data.length} days &middot; {key.replace(/_/g, " ")} &middot; peak {fmt(max)}</div>
      </div>
    );
  }
  // Breakdown → top-5 mini bars
  const lk = labelKeyOf(data[0]);
  const nk = firstNumericKey(data[0]);
  if (!nk) return <PeekEmpty />;
  const rows = data.slice(0, 5);
  const max = Math.max(1, ...rows.map(r => parseFloat(r[nk]) || 0));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {rows.map((r, i) => {
        const v = parseFloat(r[nk]) || 0;
        const label = lk ? String(r[lk]) : `#${i + 1}`;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10.5, color: C.dim, width: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}{r.medium ? `/${r.medium}` : ""}</span>
            <div style={{ flex: 1, height: 4, background: "#f0eee9", borderRadius: 2 }}>
              <div style={{ height: "100%", width: `${(v / max) * 100}%`, background: C.accent, borderRadius: 2, opacity: 0.75 }} />
            </div>
            <span style={{ fontSize: 10, color: C.faint, width: 34, textAlign: "right" }}>{fmt(v)}</span>
          </div>
        );
      })}
      {data.length > 5 && <div style={{ fontSize: 9.5, color: C.faint }}>+ {data.length - 5} more in the report</div>}
    </div>
  );
}
function GenericTable({ data, max = 12 }) {
  const rows = (Array.isArray(data) ? data : [data]).filter(Boolean).slice(0, max);
  if (!rows.length) return null;
  const cols = Object.keys(rows[0]).slice(0, 5);
  const thx = { fontSize: 9.5, color: C.dim, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, textAlign: "left", padding: "6px 8px", borderBottom: `1px solid ${C.line}` };
  const tdx = { fontSize: 11.5, color: C.ink, padding: "6px 8px", borderBottom: "1px solid #f0eee9" };
  const cell = (v) => v == null ? "\u2014" : !isNaN(parseFloat(v)) && String(v).trim() !== "" && typeof v !== "boolean"
    ? (parseFloat(v) < 1 && parseFloat(v) > 0 ? (parseFloat(v) * 100).toFixed(1) + "%" : fmt(v)) : String(v);
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>{cols.map(c => <th key={c} style={thx}>{c.replace(/_/g, " ")}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{cols.map(c => <td key={c} style={{ ...tdx, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cell(r[c])}</td>)}</tr>)}</tbody>
    </table>
  );
}

function BigChart({ data }) {
  // Time series → area-style trend; breakdown → top-10 horizontal bars
  if (data?.[0]?.metric_date) {
    const key = firstNumericKey(data[0]);
    const vals = data.map(r => parseFloat(r[key]) || 0);
    const max = Math.max(1, ...vals);
    const W = 720, H = 120;
    const pts = vals.map((v, i) => `${(i / Math.max(1, vals.length - 1)) * W},${H - 14 - (v / max) * (H - 26)}`).join(" ");
    return (
      <div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
          <polyline points={`0,${H - 14} ${pts} ${W},${H - 14}`} fill="#6366f118" stroke="none" />
          <polyline points={pts} fill="none" stroke={C.accent} strokeWidth="2" strokeLinejoin="round" />
          <text x="0" y={H - 2} fontSize="9" fill={C.faint}>{data[0].metric_date}</text>
          <text x={W} y={H - 2} fontSize="9" fill={C.faint} textAnchor="end">{data[data.length - 1].metric_date}</text>
        </svg>
        <div style={{ fontSize: 10, color: C.faint, marginTop: 3 }}>{key?.replace(/_/g, " ")} \u00b7 peak {fmt(Math.max(...vals))} \u00b7 total {fmt(vals.reduce((a, b) => a + b, 0))}</div>
      </div>
    );
  }
  const lk = labelKeyOf(data?.[0]); const nk = firstNumericKey(data?.[0]);
  if (!nk) return <GenericTable data={data} />;
  const rows = data.slice(0, 10);
  const max = Math.max(1, ...rows.map(r => parseFloat(r[nk]) || 0));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((r, i) => {
        const v = parseFloat(r[nk]) || 0;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11.5, color: C.ink, width: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lk ? String(r[lk]) : `#${i + 1}`}{r.medium ? ` / ${r.medium}` : ""}</span>
            <div style={{ flex: 1, height: 10, background: "#f0eee9", borderRadius: 4 }}>
              <div style={{ height: "100%", width: `${(v / max) * 100}%`, background: C.accent, borderRadius: 4, opacity: 0.8 }} />
            </div>
            <span style={{ fontSize: 11, color: C.dim, width: 52, textAlign: "right" }}>{fmt(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Exhibit({ label, data }) {
  const [mode, setMode] = useState("chart");
  const empty = data == null || (Array.isArray(data) && data.length === 0);
  if (empty || mode === "hidden") {
    if (empty) return null;
    return (
      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0eee9" }}>
        <span style={{ fontSize: 11.5, color: C.faint }}>{label} (hidden from report)</span>
        <button onClick={() => setMode("chart")} style={{ background: "none", border: "none", color: C.dim, fontSize: 11, cursor: "pointer", textDecoration: "underline", fontFamily: sans }}>Show</button>
      </div>
    );
  }
  const isSummary = !Array.isArray(data) || (data.length === 1 && !data[0]?.metric_date && !labelKeyOf(data[0]));
  const summaryObj = !Array.isArray(data) ? data : isSummary ? data[0] : null;
  return (
    <div style={{ marginBottom: 26, breakInside: "avoid" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, borderBottom: `1px solid ${C.line}`, paddingBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dim }}>{label}</span>
        <div className="no-print" style={{ display: "flex", gap: 2 }}>
          {(isSummary ? ["numbers"] : ["chart", "numbers"]).concat(["hidden"]).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              background: mode === m || (isSummary && m === "numbers" && mode === "chart") ? "#f0eee9" : "none",
              border: `1px solid ${C.line}`, borderRadius: 6, padding: "3px 10px", fontSize: 10.5,
              color: C.dim, cursor: "pointer", fontFamily: sans, textTransform: "capitalize" }}>{m === "hidden" ? "Hide" : m}</button>
          ))}
        </div>
      </div>
      {summaryObj
        ? <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {Object.entries(summaryObj).filter(([, v]) => v !== null && !isNaN(parseFloat(v))).slice(0, 7).map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{parseFloat(v) < 1 && parseFloat(v) > 0 && /rate|ctr/.test(k) ? (parseFloat(v) * 100).toFixed(1) + "%" : fmt(v)}</div>
                <div style={{ fontSize: 9.5, color: C.faint, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        : mode === "chart" ? <BigChart data={data} /> : <GenericTable data={data} />}
    </div>
  );
}

function PeekEmpty() { return <div style={{ fontSize: 10.5, color: "#c0503f" }}>No data in this range</div>; }

const linkBtn = { background: "none", border: "none", color: "#555", fontSize: 11.5, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, fontFamily: sans, padding: 0 };

const inp = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, padding: "9px 12px", color: C.ink, fontSize: 13, outline: "none", fontFamily: sans };
